import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Factory, HardDrive } from "lucide-react";
import { Annotation } from "@/components/Annotation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FARPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/financial/far`).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="h-64 skeleton-shimmer rounded" />;

  const comments = data.management_comments || [];
  const depreciation = data.depreciation || [];

  return (
    <div className="space-y-5" data-testid="far-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Fixed Asset Register</h1>
        <p className="text-sm text-slate-400 mt-1">Asset summary, management commentary &amp; depreciation schedule &middot; INR Millions</p>
      </div>

      {/* Management Comments */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="far-comments">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Factory className="w-4 h-4 text-amber-400" /> Management Commentary on Fixed Assets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {comments.map((c, i) => (
            <div key={i} className="glass-card rounded-md p-4 border-l-2 border-amber-600/50">
              <div className="flex items-center gap-2 mb-1.5">
                <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-800">{c.asset}</Badge>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">{c.comment}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Annotation>
        The company has ample land and building capacity to support expansion without immediate capex on real estate. Manufacturing operates at 50-60% capacity utilization — headroom to scale production for existing categories. Investment in automation is planned to enter new product manufacturing segments and improve efficiency.
      </Annotation>

      {/* Depreciation Schedule */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="depreciation-table">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-blue-400" /> Depreciation Schedule (INR Millions)
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 uppercase w-48">Asset Class</th>
                {['FY2024A', 'FY2025A', "YTD'FY2026A", 'FY2026P', 'FY2027E'].map((h) => (
                  <th key={h} className="text-right py-2 px-3 text-[10px] text-slate-500 uppercase">{h.replace('FY20', 'FY').replace("'FY20", "'FY")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {depreciation.map((d, i) => {
                const isTotal = d.asset_class === 'Total';
                return (
                  <tr key={i} className={`border-b border-slate-800/20 ${isTotal ? "bg-slate-800/20" : ""} hover:bg-slate-800/20`}>
                    <td className={`py-2 px-3 text-xs ${isTotal ? "font-semibold text-slate-200" : "text-slate-400"}`}>{d.asset_class}</td>
                    {['FY2024A', 'FY2025A', "YTD'FY2026A", 'FY2026P', 'FY2027E'].map((k) => (
                      <td key={k} className={`py-2 px-3 text-right font-mono text-[11px] ${isTotal ? "font-semibold text-slate-200" : "text-slate-400"}`}>
                        {d[k] !== undefined ? d[k].toFixed(2) : "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
